'use client';

import { useState, useEffect, useMemo } from 'react';
import { authService } from '@/lib/authService';
import {
  resolveExpenseAccountMappingCode,
  normalizeExpenseAccountMappings,
  getExpenseAccountByCode,
  type ExpenseAccountOption,
  type ExpenseAccountMappings,
} from '@/lib/finance/expenseAccountMapping';
import { getPreferredDefaultTaxRate, parseTaxRateValue } from '@/lib/finance/taxRates';
import type { DocumentTaxRate } from '@/components/finance/modals/SortableLineItem';
import type { ChildEntryType } from '../../types';
import { EXPENSE_ENTRY_TYPES } from './constants';
import type { CaptureFormData } from './captureSchema';
import { toRecordString } from './captureSchema';
import { createLogger } from '@/lib/debugLogger';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

const logger = createLogger('useExpenseAccounts');

interface UseExpenseAccountsParams {
  selectedEntryType: ChildEntryType | null;
  entryTypeProp?: ChildEntryType;
  formData: CaptureFormData;
}

export function useExpenseAccounts({
  selectedEntryType,
  entryTypeProp,
  formData,
}: UseExpenseAccountsParams) {
  const [expenseAccounts, setExpenseAccounts] = useState<ExpenseAccountOption[]>([]);
  const [expenseAccountMappings, setExpenseAccountMappings] = useState<ExpenseAccountMappings>({});
  const [taxRates, setTaxRates] = useState<DocumentTaxRate[]>([]);
  const [settingsDefaultTaxRateId, setSettingsDefaultTaxRateId] = useState<number | null>(null);

  const preferredDefaultTaxRate = useMemo(() => {
    if (settingsDefaultTaxRateId) {
      const settingsRate = taxRates.find(rate => rate.id === settingsDefaultTaxRateId);
      if (settingsRate) return settingsRate;
    }
    return getPreferredDefaultTaxRate(taxRates);
  }, [taxRates, settingsDefaultTaxRateId]);

  const displayAccountCode = useMemo(() => {
    if (!EXPENSE_ENTRY_TYPES.includes((selectedEntryType || entryTypeProp) as ChildEntryType)) {
      return null;
    }
    const formDataRecord = toRecordString(formData);
    const category = (selectedEntryType || entryTypeProp) === 'purchase'
      ? (formDataRecord.category || formDataRecord.purchase_type || 'other')
      : (selectedEntryType || entryTypeProp);
    const code = resolveExpenseAccountMappingCode(category, expenseAccountMappings, expenseAccounts);
    if (code) {
      const account = getExpenseAccountByCode(expenseAccounts, code);
      return account ? { code: account.account_code, name: account.name } : { code, name: null };
    }
    return null;
  }, [selectedEntryType, entryTypeProp, formData, expenseAccountMappings, expenseAccounts]);

  useEffect(() => {
    const loadExpenseAccountDefaults = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      try {
        const [accountsResponse, settingsResponse] = await Promise.all([
          fetch('/api/v1/chart-of-accounts', {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
          fetch('/api/v1/finance/settings', {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }),
        ]);

        if (!accountsResponse.ok) {
          throw new Error('Failed to load expense accounts');
        }

        const accountsPayload = await accountsResponse.json();
        const settingsPayload = settingsResponse.ok ? await settingsResponse.json() : null;
        const accounts = Array.isArray(accountsPayload?.data)
          ? accountsPayload.data
            .filter((account: unknown): account is ExpenseAccountOption => Boolean(
              account
              && typeof account === 'object'
              && 'account_code' in account
              && 'name' in account
            ))
            .map((account: ExpenseAccountOption) => ({
              account_code: String(account.account_code || '').trim(),
              name: String(account.name || '').trim(),
              account_type: 'account_type' in account && typeof account.account_type === 'string' ? account.account_type : null,
              account_subtype: 'account_subtype' in account && typeof account.account_subtype === 'string' ? account.account_subtype : null,
              allowed_allocation_types: 'allowed_allocation_types' in account && Array.isArray(account.allowed_allocation_types) ? account.allowed_allocation_types : null,
              is_active: 'is_active' in account && typeof account.is_active === 'boolean' ? account.is_active : null,
            }))
          : [];

        const nextCustomSettings = settingsPayload?.data?.custom_settings && typeof settingsPayload.data.custom_settings === 'object' && !Array.isArray(settingsPayload.data.custom_settings)
          ? settingsPayload.data.custom_settings as Record<string, unknown>
          : {};

        setExpenseAccounts(accounts.filter((account: ExpenseAccountOption) => account.account_code && account.name));
        setExpenseAccountMappings(normalizeExpenseAccountMappings(nextCustomSettings.expense_account_mappings));

        const fetchedDefaultTaxRateId = nextCustomSettings.default_tax_rate_id;
        if (fetchedDefaultTaxRateId != null && Number.isFinite(Number(fetchedDefaultTaxRateId)) && Number(fetchedDefaultTaxRateId) > 0) {
          setSettingsDefaultTaxRateId(Number(fetchedDefaultTaxRateId));
        }
      } catch (error) {
        if (!isSilentFetchError(error)) logger.error('[useExpenseAccounts] Failed to load expense account defaults:', error);
      }
    };

    const loadTaxRates = async () => {
      const token = authService.getTokens()?.access_token;
      if (!token) return;
      try {
        const response = await fetch('/api/v1/finance', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ action: 'getTaxRates' }),
        });
        if (response.ok) {
          const result = await response.json();
          const fetchedTaxRates: DocumentTaxRate[] = Array.isArray(result.tax_rates)
            ? result.tax_rates as DocumentTaxRate[]
            : Array.isArray(result.data)
              ? result.data as DocumentTaxRate[]
              : [];
          if (result.success) {
            setTaxRates(fetchedTaxRates.filter((rate) => rate.is_active !== false));
          }
        }
      } catch (error) {
        if (!isSilentFetchError(error)) logger.error('[useExpenseAccounts] Failed to load tax rates:', error);
      }
    };

    void loadExpenseAccountDefaults();
    void loadTaxRates();
  }, []);

  return {
    expenseAccounts,
    expenseAccountMappings,
    taxRates,
    preferredDefaultTaxRate,
    displayAccountCode,
  };
}
